import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

export type SeoPageMeta = {
  title: string;
  description: string;
  path: string;
  image?: string;
};

/** Fallback social-card image used when a page doesn't supply its own `image`. */
const DEFAULT_SOCIAL_IMAGE = '/assets/WORKNEST-ICON.display.png';

@Injectable({ providedIn: 'root' })
export class SeoMetaService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  setPageMeta(data: SeoPageMeta): void {
    this.title.setTitle(data.title);

    this.meta.updateTag({ name: 'description', content: data.description });
    this.meta.updateTag({ property: 'og:title', content: data.title });
    this.meta.updateTag({ property: 'og:description', content: data.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'WorkNest' });
    this.meta.updateTag({ property: 'og:url', content: this.absoluteUrl(data.path) });

    const imageUrl = this.resolveImageUrl(data.image);
    this.meta.updateTag({ property: 'og:image', content: imageUrl });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: data.title });
    this.meta.updateTag({ name: 'twitter:description', content: data.description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    this.setCanonical(this.absoluteUrl(data.path));
  }

  private absoluteUrl(path: string): string {
    const origin = environment.siteUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${normalizedPath}`;
  }

  private resolveImageUrl(image?: string): string {
    const candidate = image ?? DEFAULT_SOCIAL_IMAGE;
    return /^https?:\/\//i.test(candidate) ? candidate : this.absoluteUrl(candidate);
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
